import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AccessibilityInfo, Image, StyleSheet, View } from 'react-native';
import { useIsFocused } from '@react-navigation/native';
import { TWCButton } from '@components';
import axios from 'axios';
import jwt_decode from 'jwt-decode';
import { useRecoilState, useRecoilValue, useResetRecoilState } from 'recoil';

import { assets } from '@assets';
import { apiConfig, DSP_CLIENT_TOKEN_KEY, onboardingTestIDs, Theme } from '@constants';
import { useContextReference, useNodeHandle } from '@hooks';
import { navigationService } from '@services';
import { onboardingFlowState, screenReaderEnabledState, userEmailState, userSettingsState } from '@store';

import { RadialGradientBackground } from '../../components/loading/RadialGradientBackground';
import { TWCPage } from '../../components/primitives/TWCPage/TWCPage';
import { TWCText } from '../../components/primitives/TWCText/TWCText';
import { TWCTextInput } from '../../components/primitives/TWCTextInput/TWCTextInput';
import { segmentEventTypes } from '../../components/segment/segmentEventTypes';
import { SegmentIdentifyUser } from '../../components/segment/SegmentIdentifyUser';
import { segmentTrackEvent } from '../../components/segment/SegmentTrackEvent';
import { getDspAuthHeader } from '../../helpers/DspAuth';
import { canPurchaseOnPlatform, isAndroid } from '../../helpers/Platform';
import { setStorageItem } from '../../helpers/storage';
import { verifyAppInstalled } from '../../helpers/verifyAppInstalled';
import { getUserProducts } from '../../hooks/api/useGetUserProducts/useGetUserProducts';

const tPath = 'authVerificationPage';

const PageState = {
  enterCode: 'enterCode',
  checkingCode: 'checkingCode',
  errorCode: 'errorCode',
  checkingSubscription: 'checkingSubscription',
  errorSubscription: 'errorSubscription',
};

export const AuthVerificationPage = () => (
  <TWCPage
    childIds={[onboardingTestIDs.authVerification.codeEntryInput, onboardingTestIDs.authVerification.sendNewCodeButton]}
    firstFocus={onboardingTestIDs.authVerification.codeEntryInput}
    pageID={onboardingTestIDs.pages.authVerificationPage}
  >
    <RadialGradientBackground>
      <AuthVerificationPageContent />
    </RadialGradientBackground>
  </TWCPage>
);

const handleActiveUsers = async userInfo => {
  await segmentTrackEvent(segmentEventTypes.LOGIN, userInfo);
  await SegmentIdentifyUser(userInfo, false);
  await verifyAppInstalled(userInfo);

  isAndroid
    ? navigationService.replace(onboardingTestIDs.pages.successPage)
    : navigationService.replace(onboardingTestIDs.pages.deviceActivationPage);
};

const AuthVerificationPageContent = () => {
  const codeEntryInputReference = useContextReference(onboardingTestIDs.authVerification.codeEntryInput);

  const sendNewCodeButtonReference = useContextReference(onboardingTestIDs.authVerification.sendNewCodeButton);

  const codeEntryInputHandle = useNodeHandle(codeEntryInputReference);
  const sendNewCodeButtonHandle = useNodeHandle(sendNewCodeButtonReference);

  const isPageFocused = useIsFocused();

  // Controls page state flow
  const [pageState, setPageState] = useState(PageState.enterCode);

  const { t } = useTranslation('translation', { keyPrefix: tPath });

  /*
    This email will eventually get pulled in automatically after spike story in WEXF-314 is complete
  */
  const userEmail = useRecoilValue(userEmailState);
  /*
    Potentially set this to be a number over a string, depending on how we send to DSP
  */
  const [codeEntry, setCodeEntry] = useState('');
  const [userSettings, setUserSettings] = useRecoilState(userSettingsState);
  const resetUserSettings = useResetRecoilState(userSettingsState);
  const [onboardingFlow, setOnboardingFlow] = useRecoilState(onboardingFlowState);

  const screenReaderEnabled = useRecoilValue(screenReaderEnabledState);

  const handleError = error => {
    if (error.response && isPageFocused) {
      setIsPaidAuth(false);
      navigationService.navigate(onboardingTestIDs.pages.errorPage);
    }
  };

  const getNewCode = async () => {
    try {
      const getNewCodeBody = {
        client_id: apiConfig.AUTH0_CLIENT_ID,
        connection: 'email',
        email: userEmail,
        send: 'code',
        authParams: {
          scope: 'openid profile',
        },
      };

      await axios.post(apiConfig.AUTH0_START_URL, getNewCodeBody);
      if (screenReaderEnabled) {
        AccessibilityInfo.announceForAccessibility(t('authVerificationInstructions.newCodeSentAnnouncement'));
      }
    } catch (error) {
      console.log('new code error');
      handleError(error);
    }
  };

  const goBackToWebSignUp = () => {
    if (onboardingFlow.secondTime) {
      navigationService.navigate(onboardingTestIDs.pages.onboardingHomePage);
    } else {
      setOnboardingFlow({ ...onboardingFlow, secondTime: true });
      navigationService.navigate(onboardingTestIDs.pages.onboardingFreeTrialPage);
    }
  };

  const getProducts = async (retry = false) => {
    try {
      const headers = await getDspAuthHeader(retry);
      const { products, platform } = await getUserProducts(headers);

      console.log(products);
      if (products.svod.length > 0) {
        const {
          customer,
          product,
          next_assessment_at,
          trial_started_at,
          state,
          previous_state,
          activated_at,
          scheduled_cancellation_at,
        } = products.svod[0];

        const newSettings = {
          ...userSettings,
          firstName: customer.first_name,
          lastName: customer.last_name,
          email: customer.email,
          pricePlan: product.name,
          billDate: next_assessment_at,
          trialStart: trial_started_at,
          subscriptionStatus: state,
          priorSubscriptionStatus: previous_state,
          dspCustomerId: customer.id,
          isPaidSubscriber: true,
          substart: activated_at,
          scheduledcancellationat: scheduled_cancellation_at,
          subscriptionPlatform: platform,
          intervalUnit: product.interval_unit,
        };

        setUserSettings(newSettings);
        const isValid = state === 'active' || state === 'trialing';

        if (isValid) {
          handleActiveUsers(newSettings);
        } else {
          // For native flow, send users to purchase
          if (canPurchaseOnPlatform && state === 'canceled') {
            // Go to welcome back
            navigationService.navigate(onboardingTestIDs.pages.subscriberWelcomeBackPage);
          } else if (canPurchaseOnPlatform) {
            navigationService.navigate(onboardingTestIDs.pages.subscriptionSelectionPage);
          } else {
            goBackToWebSignUp();
          }
        }
      }
    } catch (productsError) {
      if (productsError.response) {
        console.log(productsError);
        // if this our first time through, and the status is 403, meaning expired token,
        // so retry (which will force a new token)
        const { status } = productsError.response;

        if (!retry && status === 403) {
          getProducts(true);
        } else {
          // regardless of the cause of the error, we need to clear user settings
          resetUserSettings();
          // if we get a 424, which means that user has no subscribtion
          // in that case we send them to the signup page
          // in the case of 404, users should always be sent back to the home page
          // otherwise we just send any unknown error to the onboarding error page
          if (status === 404) {
            navigationService.navigate(onboardingTestIDs.pages.onboardingHomePage);
          } else if (status === 424) {
            if (canPurchaseOnPlatform) {
              navigationService.navigate(onboardingTestIDs.pages.subscriptionSelectionPage);
            } else {
              goBackToWebSignUp();
            }
          } else {
            handleError(productsError);
          }
        }
      }
    }
  };

  const [isPaidAuth, setIsPaidAuth] = useState(false);

  const getPaidAuth = async () => {
    try {
      const getTokenBody = {
        grant_type: 'http://auth0.com/oauth/grant-type/passwordless/otp',
        client_id: apiConfig.AUTH0_CLIENT_ID,
        otp: codeEntry,
        realm: 'email',
        username: userEmail,
        scope: 'openid profile',
        audience: apiConfig.DSP_HOST_URL,
      };

      // TODO: ADD USER INFO INTO THE HEADER OF THE AUTH0_TOKEN_URL call

      const { data } = await axios.post(apiConfig.AUTH0_TOKEN_URL, getTokenBody);

      if (data && data?.access_token) {
        const decoded = jwt_decode(data.access_token);
        const newClientToken = decoded[apiConfig.DSP_CUSTOMER_TOKEN_KEY];

        setStorageItem(DSP_CLIENT_TOKEN_KEY, newClientToken);
        setIsPaidAuth(true);
      } else {
        handleError({ response: 'error' });
      }
    } catch (error) {
      handleError(error);
    }
  };

  const onSubmit = useCallback(() => {
    if (codeEntry.length === 6 && pageState === PageState.enterCode) {
      getPaidAuth();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [codeEntry, pageState]);

  useEffect(() => {
    if (isPaidAuth && isPageFocused) {
      getProducts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPaidAuth, isPageFocused]);

  useEffect(() => {
    onSubmit();
  }, [onSubmit]);

  const noFocusInput = isAndroid ? codeEntryInputHandle : null;
  const noFocusButton = isAndroid ? sendNewCodeButtonHandle : null;

  return (
    <View style={styles.contentWrapper}>
      <Image source={assets.twcLogo} style={styles.logoImage} testID={onboardingTestIDs.authVerification.logoImage} />
      <TWCText
        style={styles.headingText}
        testID={onboardingTestIDs.authVerification.headingText}
        variant={'mediumText50'}
      >
        {t('authVerificationHeading.headingText')}
      </TWCText>
      <TWCText
        style={styles.headingTextUserEmail}
        testID={onboardingTestIDs.authVerification.headingTextUserEmail}
        variant={'mediumText50'}
      >
        {userEmail}
      </TWCText>
      <TWCText
        style={styles.codeEntryInstructionsText}
        testID={onboardingTestIDs.authVerification.codeEntryInstructions}
        variant={'regularText36'}
      >
        {t('authVerificationInstructions.codeEntryInstructions')}
      </TWCText>
      <TWCTextInput
        accessibilityHint={t('authVerificationInstructions.accessibilityHint')}
        accessibilityLabel={t('authVerificationInstructions.accessibilityLabel', { email: userEmail })}
        borderColorDefault={styles.codeEntryInputBorder}
        clearOnBeginEditing
        hideKeyboardInput
        keyboardBackground={Theme.blackPearlBlue}
        keyboardDismissConfig={{ right: true }}
        keyboardType={'number-pad'}
        keyboardWidth={740}
        keyboardWrapConfig={{ horizontal: false }}
        nextFocusDown={sendNewCodeButtonHandle}
        nextFocusLeft={noFocusInput}
        nextFocusRight={noFocusInput}
        nextFocusUp={noFocusInput}
        numericOnly
        placeholder={t('authVerificationInstructions.codeEntryInputPlaceholderText')}
        placeholderTextColor={Theme.withOpacity(Theme.white, 0.5)}
        ref={codeEntryInputReference}
        returnKeyType={'done'}
        style={styles.codeEntryInput}
        testID={onboardingTestIDs.authVerification.codeEntryInput}
        useMiniKeyboard
        value={codeEntry}
        onChangeText={text => {
          setPageState(PageState.enterCode);
          setCodeEntry(text);
          if (text.length >= 6) {
            return true;
          }
        }}
        onSubmitEditing={onSubmit}
      />
      <TWCText
        importantForAccessibility={'no'}
        style={styles.automaticallyUpdateText}
        testID={onboardingTestIDs.authVerification.automaticallyUpdateText}
        variant={'regularText36'}
      >
        {t('authVerificationInstructions.automaticallyUpdateText')}
      </TWCText>
      <TWCButton
        accessibilityHint={t('authVerificationInstructions.newCodeAccessibilityHint', { email: userEmail })}
        accessibilityLabel={t('authVerificationInstructions.newCodeAccessibilityLabel')}
        nextFocusDown={noFocusButton}
        nextFocusLeft={noFocusButton}
        nextFocusRight={noFocusButton}
        nextFocusUp={codeEntryInputHandle}
        ref={sendNewCodeButtonReference}
        style={styles.newCodeButton}
        testID={onboardingTestIDs.authVerification.sendNewCodeButton}
        title={t('authVerificationInstructions.newCodeButtonText')}
        onPress={getNewCode}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  automaticallyUpdateText: {
    marginBottom: Theme.px(68),
    marginTop: Theme.px(140),
  },
  codeEntryInput: {
    backgroundColor: Theme.withOpacity(Theme.whisperWhite, 0.1),
    textAlign: 'center',
    width: Theme.px(385),
  },
  codeEntryInputBorder: {
    borderColor: Theme.withOpacity(Theme.whisperWhite, 0.4),
  },
  codeEntryInputFocused: {
    borderColor: Theme.dodgerBlue,
    color: Theme.white,
  },
  codeEntryInstructionsText: {
    marginBottom: Theme.px(30),
  },
  contentWrapper: {
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: Theme.px(100),
  },
  headingText: {
    lineHeight: Theme.px(66),
  },
  headingTextUserEmail: {
    marginBottom: Theme.px(18),
  },
  logoImage: {
    height: Theme.px(156),
    marginBottom: Theme.px(30),
    width: Theme.px(156),
  },
});
